import React, { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "../api";
import {
  BarChart, Bar, LineChart, Line, ComposedChart, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, LabelList,
} from "recharts";
import "./AdminPanel.css";
import "./Dashboard.css";

// ─── Category colors ─────────────────────────────────────────────────────────
const CAT_COLOR = (cat: string): string => ({
  COMPENSATION:     "#4A90E2",
  OCCUPANCY:        "#e84393",
  OPERATING:        "#ea580c",
  ADVERTISING:      "#7c3aed",
  ALLOCATED:        "#475569",
  AMORT:            "#64748b",
} as Record<string, string>)[cat.toUpperCase().replace(/\s/g, "")] ?? "#94a3b8";

const EXCLUDED_CATS = new Set(["Corp OH", "CORP_OH", "Uncategorized", "OtherAdjustments", "OTHERADJUSTMENTS"]);

// ─── Interfaces ──────────────────────────────────────────────────────────────
interface CCRow {
  cost_center:      string;
  cost_center_name: string;
  business_unit:    string;
  month_budget:   number;
  month_actual:   number;
  month_variance: number;
  ytd_budget:     number;
  ytd_actual:     number;
  ytd_variance:   number;
  qtr_budget:     number;
}

interface CategoryKPI {
  category:       string;
  month_budget:   number;
  month_actual:   number;
  month_variance: number;
  ytd_budget:     number;
  ytd_actual:     number;
  ytd_variance:   number;
  qtr_budget:     number;
  annual_budget:  number;
  forecast_total: number;
  mpy_forecast?:  number;
  month_budget_ab?: number;
  ytd_budget_ab?:   number;
  top_over:  CCRow[];
  top_under: CCRow[];
}

interface CCCatBreakdown {
  category:       string;
  month_budget:   number;
  month_actual:   number;
  month_variance: number;
  ytd_budget:     number;
  ytd_actual:     number;
  ytd_variance:   number;
  qtr_budget:     number;
  annual_budget:  number;
  forecast_total: number;
  month_budget_ab?: number;
  ytd_budget_ab?:   number;
}

interface AcctRow {
  account:        string;
  month_budget:   number;
  month_actual:   number;
  month_variance: number;
  ytd_budget:     number;
  ytd_actual:     number;
  ytd_variance:   number;
  month_budget_ab?: number;
  ytd_budget_ab?:   number;
}

interface VRAccountRow {
  account: string;
  category: string;
  month_budget: number;
  month_actual: number;
  month_variance: number;
  ytd_budget: number;
  ytd_actual: number;
  ytd_variance: number;
}

interface VRCostCenter {
  cost_center: string;
  cost_center_name: string;
  business_unit: string;
  month_budget: number;
  month_actual: number;
  month_variance: number;
  ytd_budget: number;
  ytd_actual: number;
  ytd_variance: number;
  annual_budget: number;
  forecast_total: number;
  mpy_forecast?: number;
  top_unfavorable: VRAccountRow[];
  top_favorable: VRAccountRow[];
  category_breakdown?: { category: string; month_budget: number; month_actual: number; month_variance: number; ytd_budget: number; ytd_actual: number; ytd_variance: number }[];
}

interface BUCatBreakdown {
  category:       string;
  month_budget:   number;
  month_actual:   number;
  month_variance: number;
  ytd_budget:     number;
  ytd_actual:     number;
  ytd_variance:   number;
  qtr_budget:     number;
}

interface BUSummaryRow {
  business_unit:  string;
  month_budget:   number;
  month_actual:   number;
  month_variance: number;
  ytd_budget:     number;
  ytd_actual:     number;
  ytd_variance:   number;
  qtr_budget:     number;
  annual_budget:  number;
  forecast_total: number;
  month_budget_ab?: number;
  ytd_budget_ab?:   number;
  categories:     BUCatBreakdown[];
}

interface MonthlyTrendPoint {
  month:      string;
  short:      string;
  budget:     number;
  actual:     number;
  annualBudget: number;
  hasActuals: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtMoney(n: number): string {
  const abs = Math.abs(n);
  const str = "$" + Math.round(abs).toLocaleString("en-US");
  return n < 0 ? `(${str})` : str;
}

/** Accounting notation: unfavorable (positive variance = over budget) in brackets, favorable plain */
function fmtVar(n: number): string {
  const val = fmtMoney(Math.abs(n));
  return n > 0 ? `(${val})` : val;
}

/** Accounting notation for YTG: negative YTG (over annual budget) in brackets */
function fmtYTG(n: number): string {
  const val = fmtMoney(Math.abs(n));
  return n < 0 ? `(${val})` : val;
}

function getFiscalQuarter(month: string): string {
  const q1 = ["April", "May", "June"];
  const q2 = ["July", "August", "September"];
  const q3 = ["October", "November", "December"];
  if (q1.includes(month)) return "Q1";
  if (q2.includes(month)) return "Q2";
  if (q3.includes(month)) return "Q3";
  return "Q4";
}

/** Build a structured LLM prompt from variance data for copy-paste into ChatGPT/Claude.
 *  All math is pre-computed — the AI only formats and adds 3-5 action bullets. */
function buildVariancePrompt(opts: {
  fiscalYear: string;
  periodLabel: string;
  isYTD: boolean;
  buFilter: string;
  buSummary: BUSummaryRow[];
  categories: CategoryKPI[];
  vrCostCenters: VRCostCenter[];
  annualBudget: number;
}): string {
  const { fiscalYear, periodLabel, isYTD, buFilter, buSummary, categories, vrCostCenters, annualBudget } = opts;

  const fmt = (n: number) => "$" + Math.round(Math.abs(n)).toLocaleString("en-US");
  const fmtVar = (n: number) => n >= 0 ? fmt(n) : `(${fmt(n)})`;
  const varPct = (bud: number, act: number) => {
    if (bud === 0 && act > 0) return "n/m";
    if (bud === 0) return "-";
    return `${(((bud - act) / Math.abs(bud)) * 100).toFixed(1)}%`;
  };
  const isMaterial = (bud: number, act: number) => {
    const v = Math.abs(bud - act);
    return v >= 25000 || (bud !== 0 && (v / Math.abs(bud)) >= 0.10);
  };
  const favLabel = (v: number) => v > 0 ? "favorable" : v < 0 ? "UNFAVORABLE" : "flat";

  type AcctItem = { cc: string; ccName: string; bu: string; account: string; category: string;
    mBud: number; mAct: number; mVar: number; yBud: number; yAct: number; yVar: number; };

  const allAccounts: AcctItem[] = [];
  vrCostCenters.forEach(cc => {
    (cc.top_unfavorable || []).concat(cc.top_favorable || []).forEach(a => {
      allAccounts.push({
        cc: cc.cost_center, ccName: cc.cost_center_name, bu: cc.business_unit,
        account: a.account, category: a.category,
        mBud: a.month_budget, mAct: a.month_actual, mVar: a.month_budget - a.month_actual,
        yBud: a.ytd_budget, yAct: a.ytd_actual, yVar: a.ytd_budget - a.ytd_actual,
      });
    });
  });
  const seen = new Set<string>();
  const dedupAccounts = allAccounts.filter(a => {
    const key = `${a.cc}||${a.account}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const periodVar = (a: AcctItem) => isYTD ? a.yVar : a.mVar;
  const periodBud = (a: AcctItem) => isYTD ? a.yBud : a.mBud;
  const periodAct = (a: AcctItem) => isYTD ? a.yAct : a.mAct;

  // ── Pre-compute totals ──
  const totMBud = buSummary.reduce((s, b) => s + b.month_budget, 0);
  const totMAct = buSummary.reduce((s, b) => s + b.month_actual, 0);
  const totMVar = totMBud - totMAct;
  const totYBud = buSummary.reduce((s, b) => s + b.ytd_budget, 0);
  const totYAct = buSummary.reduce((s, b) => s + b.ytd_actual, 0);
  const totYVar = totYBud - totYAct;

  // ── Pre-build CC hotspot bullets (material only, sorted by |variance|) ──
  const materialCCs = [...vrCostCenters]
    .map(cc => {
      const mV = cc.month_budget - cc.month_actual;
      const yV = cc.ytd_budget - cc.ytd_actual;
      const ccAccts = dedupAccounts
        .filter(a => a.cc === cc.cost_center)
        .sort((a, b) => Math.abs(periodVar(b)) - Math.abs(periodVar(a)));
      const topDriver = ccAccts[0];
      return { ...cc, mV, yV, topDriver };
    })
    .filter(cc => isMaterial(
      isYTD ? cc.ytd_budget : cc.month_budget,
      isYTD ? cc.ytd_actual : cc.month_actual
    ))
    .sort((a, b) => Math.abs(isYTD ? b.yV : b.mV) - Math.abs(isYTD ? a.yV : a.mV))
    .slice(0, 10);

  // ── Pre-build unbudgeted spend flags ──
  const unbudgeted = dedupAccounts
    .filter(a => periodBud(a) === 0 && periodAct(a) > 0)
    .sort((a, b) => periodAct(b) - periodAct(a))
    .slice(0, 5);

  // ═══════════════════════════════════════════════════════════════════════════
  // BUILD PROMPT — pre-formatted sections the AI presents as-is
  // ═══════════════════════════════════════════════════════════════════════════
  const lines: string[] = [];

  lines.push(`Below is a pre-computed variance summary for Daikin Applied, ${fiscalYear.toUpperCase()}, ${periodLabel}.`);
  lines.push(`Variance = Budget − Actual. Positive = favorable (underspend). Negative shown in parentheses = unfavorable (overspend).`);
  lines.push(buFilter ? `Business Unit: ${buFilter}` : `Scope: All Business Units`);
  lines.push(``);

  // ── Section 1: Overall position (pre-built bullets) ──
  lines.push(`== OVERALL POSITION ==`);
  lines.push(`• ${periodLabel}: Budget ${fmt(totMBud)} | Actual ${fmt(totMAct)} | Variance ${fmtVar(totMVar)} (${varPct(totMBud, totMAct)}) — ${favLabel(totMVar)}`);
  if (!isYTD) {
    lines.push(`• YTD: Budget ${fmt(totYBud)} | Actual ${fmt(totYAct)} | Variance ${fmtVar(totYVar)} (${varPct(totYBud, totYAct)}) — ${favLabel(totYVar)}`);
  }
  lines.push(`• Annual Budget: ${fmt(annualBudget)}`);
  lines.push(``);

  // ── Section 2: BU breakdown (pre-built bullets) ──
  if (buSummary.length > 1) {
    lines.push(`== BUSINESS UNIT BREAKDOWN ==`);
    buSummary.forEach(b => {
      const mV = b.month_budget - b.month_actual;
      const yV = b.ytd_budget - b.ytd_actual;
      lines.push(`• ${b.business_unit}: ${periodLabel} Budget ${fmt(b.month_budget)} | Actual ${fmt(b.month_actual)} | Var ${fmtVar(mV)} — ${favLabel(mV)}`);
      if (!isYTD) {
        lines.push(`  YTD: Budget ${fmt(b.ytd_budget)} | Actual ${fmt(b.ytd_actual)} | Var ${fmtVar(yV)} — ${favLabel(yV)}`);
      }
    });
    lines.push(``);
  }

  // ── Section 3: Category table (pre-built) ──
  lines.push(`== CATEGORY VARIANCE ==`);
  lines.push(`Category | ${periodLabel} Var | YTD Var`);
  categories
    .filter(c => !EXCLUDED_CATS.has(c.category))
    .forEach(c => {
      const mV = c.month_budget - c.month_actual;
      const yV = c.ytd_budget - c.ytd_actual;
      lines.push(`${c.category} | ${fmtVar(mV)} | ${fmtVar(yV)}`);
    });
  lines.push(``);

  // ── Section 3: CC hotspots (pre-built bullets) ──
  lines.push(`== COST CENTER HOTSPOTS (material: >$25K or >10%) ==`);
  if (materialCCs.length === 0) {
    lines.push(`(none above threshold)`);
  }
  materialCCs.forEach(cc => {
    const driver = cc.topDriver
      ? ` — driven by ${cc.topDriver.account} (${fmtVar(periodVar(cc.topDriver))}${periodBud(cc.topDriver) === 0 ? ", UNBUDGETED" : ""})`
      : "";
    lines.push(`• CC ${cc.cost_center} (${cc.cost_center_name}): ${periodLabel} ${fmtVar(cc.mV)} | YTD ${fmtVar(cc.yV)}${driver}`);
  });
  lines.push(``);

  // ── Section 4: Unbudgeted spend flags (pre-built) ──
  if (unbudgeted.length > 0) {
    lines.push(`== UNBUDGETED SPEND (no budget, actual > $0) ==`);
    unbudgeted.forEach(a => {
      lines.push(`• ${a.account} | CC ${a.cc} (${a.ccName}) | ${periodLabel} Actual: ${fmt(periodAct(a))}`);
    });
    lines.push(``);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // LLM INSTRUCTIONS — minimal, no math required
  // ═══════════════════════════════════════════════════════════════════════════

  lines.push(`== INSTRUCTIONS ==`);
  lines.push(`Format the data above into a concise executive email for VP/C-suite. All numbers are pre-computed — do NOT recalculate or reinterpret any variance.`);
  lines.push(``);
  lines.push(`Structure:`);
  lines.push(`1. Subject line — one sentence: month, overall variance, favorable or unfavorable.`);
  lines.push(`2. Overall Position — copy the bullets above as-is.`);
  lines.push(`3. Business Unit Breakdown — copy the BU bullets. Bold each BU name.`);
  lines.push(`4. Category Table — render as a formatted table with borders. Copy the numbers exactly.`);
  lines.push(`5. CC Hotspots — copy the bullets above. Bold the CC name and variance.`);
  lines.push(`6. Action Items — write 3-5 SHORT bullets: what CC needs review, who (CC owner), and why. One line each.`);
  lines.push(``);
  lines.push(`Rules:`);
  lines.push(`• Copy all numbers exactly as shown. Do NOT add, subtract, or re-derive any figure.`);
  lines.push(`• Unfavorable amounts stay in parentheses: ($50,000). Do not change the sign format.`);
  lines.push(`• Bullet points only. No paragraphs, no narrative, no filler sentences.`);
  lines.push(`• Output as rich-text / HTML ready to paste into Outlook or Word. No markdown.`);

  return lines.join("\n");
}

// ─── Component Props ─────────────────────────────────────────────────────────

interface ActualsDashboardProps {
  apiPrefix: string;        // "/admin" for admin, "/team" for leadership
  fiscalYear: string;       // e.g. "FY26"
  ccsByBU?: Record<string, { cost_center_number: string; cost_center_name: string }[]>;
}

const ActualsDashboard: React.FC<ActualsDashboardProps> = ({ apiPrefix, fiscalYear, ccsByBU: ccsByBUProp }) => {
  // ── State ──────────────────────────────────────────────────────────────────
  const [dashMonth, setDashMonth] = useState<string>("");
  const [dashAvailableMonths, setDashAvailableMonths] = useState<string[]>([]);
  const [dashBU, setDashBU] = useState<string>("");
  const [dashSelectedCCs, setDashSelectedCCs] = useState<string[]>([]);
  const [dashBusinessUnits, setDashBusinessUnits] = useState<string[]>([]);
  const [dashBUSummary, setDashBUSummary] = useState<BUSummaryRow[]>([]);
  const [dashData, setDashData] = useState<CategoryKPI[]>([]);
  const [dashLoading, setDashLoading] = useState(false);
  const [dashMonthsLoading, setDashMonthsLoading] = useState(false);
  const [drillCC, setDrillCC] = useState<string | null>(null);
  const [drillCategory, setDrillCategory] = useState<string | null>(null);
  const [drillData, setDrillData] = useState<AcctRow[] | null>(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [dashMonthlyTrend, setDashMonthlyTrend] = useState<MonthlyTrendPoint[] | null>(null);
  const [dashAnnualBudgetTotal, setDashAnnualBudgetTotal] = useState<number>(0);
  const [dashCCSummary, setDashCCSummary] = useState<{cost_center: string; cost_center_name: string; business_unit: string; month_budget: number; month_actual: number; ytd_budget: number; ytd_actual: number; month_variance: number; ytd_variance: number; qtr_budget: number; annual_budget: number; forecast_total: number; month_budget_ab: number; ytd_budget_ab: number; categories: CCCatBreakdown[]}[]>([]);
  const [expandedBUs, setExpandedBUs] = useState<Set<string>>(new Set());
  const [expandedCCs, setExpandedCCs] = useState<Set<string>>(new Set());
  type VarSortCol = "name" | "mAB" | "mFC" | "mAc" | "mVB" | "mVF" | "yAB" | "yFC" | "yAc" | "yVB" | "yVF";
  const [ccSortCol, setCCSortCol] = useState<VarSortCol>("name");
  const [ccSortDir, setCCSortDir] = useState<1 | -1>(1);
  const [catSortCol, setCatSortCol] = useState<VarSortCol>("name");
  const [catSortDir, setCatSortDir] = useState<1 | -1>(1);
  const [acctSearch, setAcctSearch] = useState("");
  const [acctFilterTags, setAcctFilterTags] = useState<string[]>([]);
  const [allAccounts, setAllAccounts] = useState<{account: string; category: string}[]>([]);
  const [acctDropdownOpen, setAcctDropdownOpen] = useState(false);
  const acctInputRef = React.useRef<HTMLInputElement>(null);
  const acctDropRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.get(`/admin/accounts?fiscalYear=${fiscalYear || "FY26"}`)
      .then(res => setAllAccounts(res.data || []))
      .catch(() => {});
  }, [fiscalYear]);

  useEffect(() => {
    if (!acctDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (acctDropRef.current && !acctDropRef.current.contains(e.target as Node)) setAcctDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [acctDropdownOpen]);

  const acctSuggestions = useMemo(() => {
    const q = acctSearch.trim().toLowerCase();
    if (!q) return allAccounts.slice(0, 20);
    return allAccounts.filter(a => a.account.toLowerCase().includes(q)).slice(0, 20);
  }, [acctSearch, allAccounts]);

  const acctFilterLower = acctFilterTags.map(t => t.toLowerCase());
  const matchesAcctFilter = useCallback((account: string) => {
    if (acctFilterTags.length === 0) return true;
    const lower = account.toLowerCase();
    return acctFilterLower.some(tag => lower.includes(tag));
  }, [acctFilterTags, acctFilterLower]);

  const varSortVal = (d: any, col: VarSortCol): number | string => {
    const mAB = d.month_budget_ab || 0, mFC = d.month_budget || 0, mAc = d.month_actual || 0;
    const yAB = d.ytd_budget_ab || 0, yFC = d.ytd_budget || 0, yAc = d.ytd_actual || 0;
    switch (col) {
      case "name": return (d.cost_center_name || d.category || d.business_unit || "").toLowerCase();
      case "mAB": return mAB; case "mFC": return mFC; case "mAc": return mAc;
      case "mVB": return mAc - mAB; case "mVF": return mAc - mFC;
      case "yAB": return yAB; case "yFC": return yFC; case "yAc": return yAc;
      case "yVB": return yAc - yAB; case "yVF": return yAc - yFC;
    }
  };
  const varSorter = (col: VarSortCol, dir: 1 | -1) => (a: any, b: any) => {
    const av = varSortVal(a, col), bv = varSortVal(b, col);
    if (typeof av === "string" && typeof bv === "string") return av.localeCompare(bv) * dir;
    return ((av as number) - (bv as number)) * dir;
  };
  const toggleCCSort = (col: VarSortCol) => {
    if (ccSortCol === col) setCCSortDir(d => d === 1 ? -1 : 1);
    else { setCCSortCol(col); setCCSortDir(col === "name" ? 1 : -1); }
  };
  const toggleCatSort = (col: VarSortCol) => {
    if (catSortCol === col) setCatSortDir(d => d === 1 ? -1 : 1);
    else { setCatSortCol(col); setCatSortDir(col === "name" ? 1 : -1); }
  };
  const sortArrow = (col: VarSortCol, activeCol: VarSortCol, dir: 1 | -1) =>
    activeCol === col ? (dir === 1 ? " ▲" : " ▼") : "";
  const [trendChartOpen, setTrendChartOpen] = useState(false);
  const [trendChartType, setTrendChartType] = useState<"bar" | "line">("bar");
  const [trendCategory, setTrendCategory] = useState<string | null>(null);

  // ── Variance Prompt Modal ──────────────────────────────────────────────────
  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [promptText, setPromptText] = useState("");
  const [promptLoading, setPromptLoading] = useState(false);
  const [promptCopied, setPromptCopied] = useState(false);

  // ── Admin Monthly Comment (per cost center) ────────────────────────────────
  const [ccComments, setCCComments] = useState<Record<string, string>>({});
  const [ccCommentDrafts, setCCCommentDrafts] = useState<Record<string, string>>({});
  const [ccCommentSaving, setCCCommentSaving] = useState<Record<string, boolean>>({});
  const [ccCommentFeedback, setCCCommentFeedback] = useState<Record<string, string>>({});
  const [ccCommentOpen, setCCCommentOpen] = useState<string | null>(null);
  const isAdmin = apiPrefix === "/admin";

  useEffect(() => {
    if (!dashMonth || !fiscalYear) return;
    api.get(`${isAdmin ? "/admin" : "/settings"}/monthly-comments`, { params: { fy: fiscalYear, month: dashMonth } })
      .then(r => {
        const comments: Record<string, string> = r.data.comments || {};
        setCCComments(comments);
        setCCCommentDrafts(comments);
      })
      .catch(() => {});
  }, [dashMonth, fiscalYear, isAdmin]);

  const saveCCComment = (cc: string) => {
    setCCCommentSaving(prev => ({ ...prev, [cc]: true }));
    setCCCommentFeedback(prev => ({ ...prev, [cc]: "" }));
    const comment = ccCommentDrafts[cc] || "";
    api.put("/admin/monthly-comment", { fy: fiscalYear, month: dashMonth, cost_center: cc, comment })
      .then(() => {
        setCCComments(prev => ({ ...prev, [cc]: comment }));
        setCCCommentFeedback(prev => ({ ...prev, [cc]: "Saved" }));
        setTimeout(() => setCCCommentFeedback(prev => ({ ...prev, [cc]: "" })), 2000);
      })
      .catch(() => setCCCommentFeedback(prev => ({ ...prev, [cc]: "Failed" })))
      .finally(() => setCCCommentSaving(prev => ({ ...prev, [cc]: false })));
  };

  // CCs by BU — use prop if provided, otherwise fetch
  const [ccsByBULocal, setCCsByBULocal] = useState<Record<string, { cost_center_number: string; cost_center_name: string }[]>>({});
  const ccsByBU = ccsByBUProp || ccsByBULocal;

  useEffect(() => {
    if (!ccsByBUProp) {
      api.get(`${apiPrefix}/cost-centers-by-bu`, { params: { fy: fiscalYear } })
        .then(res => setCCsByBULocal(res.data.by_bu || {}))
        .catch(() => {});
    }
  }, [apiPrefix, ccsByBUProp, fiscalYear]);

  // ── Data loading ───────────────────────────────────────────────────────────
  const doLoadDashboard = useCallback((month: string, bu: string, selectedCCs: string[] = [], ytd = false, accounts: string[] = []) => {
    if (!month) return;
    setDashLoading(true);
    setDashData([]);
    setDashBUSummary([]);
    setDrillData(null);
    setDrillCC(null);
    setDrillCategory(null);
    setExpandedCCs(new Set());
    const params = new URLSearchParams({ month });
    if (bu) params.set("bu", bu);
    if (selectedCCs.length > 0) params.set("cc", selectedCCs.join(","));
    if (accounts.length > 0) params.set("accounts", accounts.join("||"));
    if (fiscalYear) params.set("fiscalYear", fiscalYear);
    if (ytd) params.set("ytd", "1");
    api.get(`${apiPrefix}/bu-dashboard?${params}`)
      .then(res => {
        setDashData(res.data.categories || []);
        setDashBusinessUnits(res.data.business_units || []);
        setDashBUSummary(res.data.bu_summary || []);
        setDashCCSummary(res.data.cc_summary || []);
        setDashLoading(false);
      })
      .catch(() => setDashLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fiscalYear, apiPrefix]);

  // Fetch monthly trend (separate so it re-runs on category filter change)
  useEffect(() => {
    const trendParams = new URLSearchParams();
    if (dashBU) trendParams.set("bu", dashBU);
    if (dashSelectedCCs.length > 0) trendParams.set("cc", dashSelectedCCs.join(","));
    if (fiscalYear) trendParams.set("fiscalYear", fiscalYear);
    if (trendCategory) trendParams.set("category", trendCategory);
    api.get(`${apiPrefix}/bu-dashboard/monthly-trend?${trendParams}`)
      .then(res => {
        setDashMonthlyTrend(res.data.months || []);
        setDashAnnualBudgetTotal(res.data.annual_budget_total || 0);
      })
      .catch(() => { setDashMonthlyTrend(null); setDashAnnualBudgetTotal(0); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fiscalYear, apiPrefix, dashBU, dashSelectedCCs, trendCategory]);

  useEffect(() => {
    if (dashMonth) doLoadDashboard(dashMonth, dashBU, dashSelectedCCs, dashMonth === "YTD", acctFilterTags);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acctFilterTags]);

  const doLoadAvailableMonths = useCallback((bu: string) => {
    setDashMonthsLoading(true);
    const params = new URLSearchParams();
    if (bu) params.set("bu", bu);
    if (fiscalYear) params.set("fy", fiscalYear);
    api.get(`${apiPrefix}/bu-dashboard/available-months?${params}`)
      .then(res => {
        const months: string[] = res.data.months || [];
        setDashAvailableMonths(months);
        const latest = months[months.length - 1] || "";
        setDashMonth(latest);
        setDashMonthsLoading(false);
        if (latest) doLoadDashboard(latest, bu);
      })
      .catch(() => setDashMonthsLoading(false));
  }, [doLoadDashboard, fiscalYear, apiPrefix]);

  // Auto-load on mount
  useEffect(() => {
    if (dashAvailableMonths.length === 0 && !dashMonthsLoading) {
      doLoadAvailableMonths(dashBU);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-select BU when there is exactly one
  useEffect(() => {
    if (dashBusinessUnits.length === 1 && !dashBU) {
      setDashBU(dashBusinessUnits[0]);
    }
  }, [dashBusinessUnits, dashBU]);

  // ── Projected Year-End ─────────────────────────────────────────────────────
  const dashProjection = useMemo(() => {
    if (!dashMonthlyTrend || dashMonthlyTrend.length === 0) return null;
    const actMonths = dashMonthlyTrend.filter(p => p.hasActuals);
    const ytdActualTotal = actMonths.reduce((s, p) => s + (p.actual || 0), 0);
    const n = actMonths.length;
    const remaining = dashMonthlyTrend.filter(p => !p.hasActuals);
    const remainingBudgetTotal = remaining.reduce((s, p) => s + (p.budget || 0), 0);
    const forecastTotal = dashMonthlyTrend.reduce((s, p) => s + (p.budget || 0), 0);
    const projectedYearEnd = n === 0 ? forecastTotal : ytdActualTotal + remainingBudgetTotal;

    return {
      forecastTotal,
      ytdActualTotal,
      annualBudgetTotal: dashAnnualBudgetTotal,
      projectedYearEnd: Math.round(projectedYearEnd),
      projectionVariance: Math.round(projectedYearEnd - forecastTotal),
      budgetVariance: Math.round(projectedYearEnd - forecastTotal),
      actualMonths: n,
      remainingMonths: remaining.length,
    };
  }, [dashMonthlyTrend, dashAnnualBudgetTotal]);

  const doLoadDrilldown = (category: string, cc?: string) => {
    if (drillCategory === category && ((!cc && !drillCC) || (cc && drillCC === cc))) {
      setDrillData(null);
      setDrillCC(null);
      setDrillCategory(null);
      return;
    }
    setDrillCategory(category);
    setDrillCC(cc || null);
    setDrillLoading(true);
    setDrillData([]);
    const effectiveMon = dashMonth === "YTD" ? dashAvailableMonths[dashAvailableMonths.length - 1] : dashMonth;
    const params = new URLSearchParams({ category, month: effectiveMon });
    if (cc) params.set("cc", cc);
    if (dashMonth === "YTD") params.set("ytd", "1");
    if (fiscalYear) params.set("fiscalYear", fiscalYear);
    api.get(`${apiPrefix}/bu-dashboard/cc-detail?${params}`)
      .then(res => { setDrillData(res.data.accounts || []); setDrillLoading(false); })
      .catch(() => setDrillLoading(false));
  };

  // ── Variance Prompt Generation ──────────────────────────────────────────────
  const handleGeneratePrompt = async () => {
    if (!dashMonth || dashLoading) return;
    setPromptLoading(true);
    setPromptModalOpen(true);
    setPromptCopied(false);

    const isYTD = dashMonth === "YTD";
    const effectiveMonth = isYTD
      ? dashAvailableMonths[dashAvailableMonths.length - 1]
      : dashMonth;
    const periodLabel = isYTD ? "YTD" : dashMonth;

    try {
      const params = new URLSearchParams({ month: effectiveMonth });
      if (dashBU) params.set("bu", dashBU);
      if (dashSelectedCCs.length > 0) params.set("cc", dashSelectedCCs.join(","));
      if (fiscalYear) params.set("fiscalYear", fiscalYear);
      if (isYTD) params.set("ytd", "1");

      const res = await api.get(`${apiPrefix}/bu-dashboard/variance-report?${params}`);
      const vrCostCenters: VRCostCenter[] = res.data.cost_centers || [];

      const prompt = buildVariancePrompt({
        fiscalYear,
        periodLabel,
        isYTD,
        buFilter: dashBU,
        buSummary: dashBUSummary,
        categories: dashData,
        vrCostCenters,
        annualBudget: dashAnnualBudgetTotal,
      });

      setPromptText(prompt);
    } catch {
      setPromptText("Error: Failed to load variance data. Please try again.");
    } finally {
      setPromptLoading(false);
    }
  };

  const BUDGET_ONLY_MONTHS = new Set(["April", "May", "June"]);
  const isForecastMonth = dashMonth && dashMonth !== "YTD" && !BUDGET_ONLY_MONTHS.has(dashMonth);
  const refWord = isForecastMonth ? "Forecast" : "Budget";

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <span className="material-icons">bar_chart</span>
        <span className="admin-section-title">Actuals Dashboard</span>
        <span className="admin-section-subtitle">{refWord} vs Actuals · month &amp; YTD</span>
        <div style={{ marginLeft: "auto", display: "flex", gap: "8px", alignItems: "center" }}>
          {/* BU filter */}
          {dashBusinessUnits.length > 0 && (
            <select
              className="admin-select"
              value={dashBU}
              onChange={e => {
                const bu = e.target.value;
                setDashBU(bu);
                setDashSelectedCCs([]);
                setDashAvailableMonths([]);
                setDashMonth("");
                doLoadAvailableMonths(bu);
              }}
            >
              <option value="">All Business Units</option>
              {dashBusinessUnits.map(bu => <option key={bu} value={bu}>{bu}</option>)}
            </select>
          )}
          {/* Month filter */}
          {dashAvailableMonths.length > 0 && (
            <select
              className="admin-select"
              value={dashMonth}
              onChange={e => {
                const val = e.target.value;
                setDashMonth(val);
                if (val === "YTD") {
                  const latest = dashAvailableMonths[dashAvailableMonths.length - 1];
                  if (latest) doLoadDashboard(latest, dashBU, dashSelectedCCs, true, acctFilterTags);
                } else {
                  doLoadDashboard(val, dashBU, dashSelectedCCs, false, acctFilterTags);
                }
              }}
            >
              {dashAvailableMonths.map(m => <option key={m} value={m}>{m}</option>)}
              <option value="YTD">YTD</option>
            </select>
          )}
          {dashMonth && dashBUSummary.length > 0 && (
            <button
              className="admin-btn admin-btn--ghost"
              onClick={handleGeneratePrompt}
              disabled={promptLoading}
              title="Generate a variance report prompt for AI analysis"
            >
              <span className="material-icons">auto_awesome</span>
              {promptLoading ? "Generating..." : "AI Prompt"}
            </button>
          )}
          <button className="admin-btn admin-btn--ghost" onClick={() => {
            const eff = dashMonth === "YTD" ? dashAvailableMonths[dashAvailableMonths.length - 1] : dashMonth;
            doLoadDashboard(eff, dashBU, dashSelectedCCs, dashMonth === "YTD", acctFilterTags);
          }}>
            <span className="material-icons">refresh</span>
            Refresh
          </button>
        </div>
      </div>

      {/* ── CC filter pills ── */}
      {dashBU && ccsByBU[dashBU] && ccsByBU[dashBU].length > 0 && (
        <div className="admin-cc-filter-bar">
          <span className="admin-cc-filter-label">Cost Centers:</span>
          <button
            className={`admin-cc-pill${dashSelectedCCs.length === 0 ? " admin-cc-pill--active" : ""}`}
            onClick={() => { setDashSelectedCCs([]); if (dashMonth) doLoadDashboard(dashMonth, dashBU, [], false, acctFilterTags); }}
          >All</button>
          {ccsByBU[dashBU].map(cc => (
            <button
              key={cc.cost_center_number}
              className={`admin-cc-pill${dashSelectedCCs.includes(cc.cost_center_number) ? " admin-cc-pill--active" : ""}`}
              title={cc.cost_center_name}
              onClick={() => {
                const next = dashSelectedCCs.includes(cc.cost_center_number)
                  ? dashSelectedCCs.filter(c => c !== cc.cost_center_number)
                  : [...dashSelectedCCs, cc.cost_center_number];
                setDashSelectedCCs(next);
                if (dashMonth) doLoadDashboard(dashMonth, dashBU, next, false, acctFilterTags);
              }}
            >{cc.cost_center_number}</button>
          ))}
        </div>
      )}

      {/* ── Budget Reference Dimension Banner ── */}
      {dashMonth && !dashMonthsLoading && !dashLoading && (() => {
        const annualDim = fiscalYear ? `Annual Budget ${fiscalYear.toUpperCase()}` : "Annual Budget";
        const PERIODS = [
          { dim: annualDim,            months: ["April","May","June"],                            range: "Apr–Jun" },
          { dim: "July Forecast",      months: ["July","August","September"],                     range: "Jul–Sep" },
          { dim: "October Forecast",   months: ["October","November","December"],                 range: "Oct–Dec" },
          { dim: "January Forecast",   months: ["January","February","March"],                    range: "Jan–Mar" },
        ];
        if (dashMonth === "YTD") {
          const latest = dashAvailableMonths[dashAvailableMonths.length - 1];
          const FISCAL = ["April","May","June","July","August","September","October","November","December","January","February","March"];
          const latestIdx = FISCAL.indexOf(latest);
          const usedPeriods = PERIODS.filter(p => p.months.some(m => FISCAL.indexOf(m) <= latestIdx));
          return (
            <div className="admin-dash-ref-bar">
              <span className="material-icons" style={{ fontSize: 14, color: "#6b7280" }}>info</span>
              <span style={{ color: "#6b7280", fontWeight: 600 }}>Forecast Reference (YTD through {latest}):</span>
              {usedPeriods.map(p => (
                <span key={p.dim} className="admin-dash-ref-chip">{p.dim} <span style={{ fontWeight: 400, opacity: 0.7 }}>({p.range})</span></span>
              ))}
            </div>
          );
        }
        const refPeriod = PERIODS.find(p => p.months.includes(dashMonth));
        if (!refPeriod) return null;
        return (
          <div className="admin-dash-ref-bar">
            <span className="material-icons" style={{ fontSize: 14, color: "#6b7280" }}>info</span>
            <span style={{ color: "#6b7280", fontWeight: 600 }}>{refPeriod.dim.includes("Forecast") ? "Forecast" : "Budget"} Reference for {dashMonth}:</span>
            <span className="admin-dash-ref-chip">{refPeriod.dim}</span>
          </div>
        );
      })()}

      {dashMonthsLoading || dashLoading ? (
        <div style={{ padding: "24px", color: "#9ca3af", fontSize: "13px" }}>Loading…</div>
      ) : !dashMonth ? (
        <div style={{ padding: "24px", color: "#9ca3af", fontSize: "13px" }}>
          No actuals uploaded yet. Upload actuals to see this dashboard.
        </div>
      ) : dashData.length === 0 ? (
        <div style={{ padding: "24px", color: "#9ca3af", fontSize: "13px" }}>
          No data for <strong>{dashMonth}</strong>. Upload a budget to see dashboard data.
        </div>
      ) : (
        <>
          {/* ── KPI Summary Strip ── */}
          {dashProjection && (() => {
            const isYTD = dashMonth === "YTD";
            const periodLabel = isYTD ? "YTD" : dashMonth;
            const periodBudget = dashData.filter(cat => !EXCLUDED_CATS.has(cat.category))
              .reduce((s, cat) => s + (isYTD ? cat.ytd_budget : cat.month_budget), 0);
            const periodActual = dashData.filter(cat => !EXCLUDED_CATS.has(cat.category))
              .reduce((s, cat) => s + (isYTD ? cat.ytd_actual : cat.month_actual), 0);
            const periodVariance = periodActual - periodBudget;
            const periodIsOver = periodVariance > 0;
            const annBud = dashProjection.annualBudgetTotal || 0;
            const ytdBud = dashData.filter(cat => !EXCLUDED_CATS.has(cat.category))
              .reduce((s, cat) => s + cat.ytd_budget, 0);
            const ytdAct = dashData.filter(cat => !EXCLUDED_CATS.has(cat.category))
              .reduce((s, cat) => s + cat.ytd_actual, 0);
            const ytdUtilPct = annBud > 0 ? (ytdAct / annBud) * 100 : 0;
            const ytdVariance = ytdAct - ytdBud;
            const ytdIsOver = ytdVariance > 0;
            return (
            <>
            <div className="dash-kpi-ref">{refWord} Reference: {isForecastMonth ? `${dashMonth} Forecast` : "Annual Budget"} {fiscalYear ? fiscalYear.toUpperCase() : ""}</div>
            <div className="dash-kpi-strip">
              <div className="dash-kpi-card">
                <div className="dash-kpi-label">
                  <span className="material-icons">account_balance</span>
                  {periodLabel} {refWord}
                </div>
                <div className="dash-kpi-value">{fmtMoney(periodBudget)}</div>
                <div className="dash-kpi-sub">
                  {isYTD ? `Budget Apr \u2013 ${dashAvailableMonths[dashAvailableMonths.length - 1] || "\u2014"}` : `${dashMonth} ${refWord.toLowerCase()}`}
                </div>
              </div>
              <div className="dash-kpi-card">
                <div className="dash-kpi-label">{periodLabel} Actual</div>
                <div className="dash-kpi-value">{fmtMoney(periodActual)}</div>
                <div className="dash-kpi-sub">
                  {isYTD ? `Apr \u2013 ${dashAvailableMonths[dashAvailableMonths.length - 1] || "\u2014"}` : `${dashMonth} actual spend`}
                </div>
              </div>
              <div className={`dash-kpi-card ${periodIsOver ? "dash-kpi-card--warn" : "dash-kpi-card--accent"}`}>
                <div className={`dash-kpi-label ${periodIsOver ? "dash-kpi-label--red" : "dash-kpi-label--green"}`}>
                  <span className="material-icons">{periodIsOver ? "warning" : "check_circle"}</span>
                  {periodIsOver ? `Over ${refWord}` : `Under ${refWord}`}
                </div>
                <div className={`dash-kpi-value ${periodIsOver ? "dash-kpi-value--red" : "dash-kpi-value--green"}`}>
                  {fmtVar(periodVariance)}
                </div>
                <div className={`dash-kpi-sub ${periodIsOver ? "dash-kpi-sub--red" : "dash-kpi-sub--green"}`}>
                  {periodLabel} actual vs {refWord.toLowerCase()}
                </div>
              </div>
              <div className="dash-kpi-card">
                <div className="dash-kpi-label">
                  <span className="material-icons">payments</span>
                  YTD Actuals
                </div>
                <div className="dash-kpi-value">{fmtMoney(ytdAct)}</div>
                <div className="dash-kpi-sub">Year-to-date spend</div>
              </div>
              <div className={`dash-kpi-card ${ytdIsOver ? "dash-kpi-card--warn" : "dash-kpi-card--accent"}`}>
                <div className={`dash-kpi-label ${ytdIsOver ? "dash-kpi-label--red" : "dash-kpi-label--green"}`}>
                  YTD Variance
                </div>
                <div className={`dash-kpi-value ${ytdIsOver ? "dash-kpi-value--red" : "dash-kpi-value--green"}`}>
                  {fmtVar(ytdVariance)}
                </div>
                <div className={`dash-kpi-sub ${ytdIsOver ? "dash-kpi-sub--red" : "dash-kpi-sub--green"}`}>
                  YTD actual vs {refWord.toLowerCase()}
                </div>
              </div>
              <div className="dash-kpi-card">
                <div className="dash-kpi-label">
                  <span className="material-icons">compare_arrows</span>
                  MPY vs Annual Budget
                </div>
                <div className="dash-kpi-vs">
                  <span className="dash-kpi-value">{fmtMoney(dashProjection.projectedYearEnd)}</span>
                  <div className="dash-kpi-vs-row2">
                    <span className="dash-kpi-vs-sep">vs</span>
                    <span className="dash-kpi-vs-secondary">{fmtMoney(annBud)}</span>
                  </div>
                </div>
                <div className="dash-kpi-sub">Projected vs Annual Budget</div>
              </div>
              {(() => {
                const projVar = dashProjection.projectedYearEnd - annBud;
                const projIsOver = projVar > 0;
                return (
                  <div className={`dash-kpi-card ${projIsOver ? "dash-kpi-card--warn" : "dash-kpi-card--accent"}`}>
                    <div className={`dash-kpi-label ${projIsOver ? "dash-kpi-label--red" : "dash-kpi-label--green"}`}>
                      MPY Variance
                    </div>
                    <div className={`dash-kpi-value ${projIsOver ? "dash-kpi-value--red" : "dash-kpi-value--green"}`}>
                      {fmtVar(projVar)}
                    </div>
                    <div className={`dash-kpi-sub ${projIsOver ? "dash-kpi-sub--red" : "dash-kpi-sub--green"}`}>
                      Projected vs Annual Budget
                    </div>
                  </div>
                );
              })()}
            </div>
            </>
            );
          })()}

          {/* ── Collapsible Monthly Trend Chart ── */}
          {dashMonthlyTrend && dashMonthlyTrend.length > 0 && (() => {
            const fmtAxis = (v: number) => {
              if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
              if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
              return `$${v}`;
            };
            const fmtBarLabel = (v: number) => {
              if (!v) return "";
              if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
              if (Math.abs(v) >= 1_000) return `$${Math.round(v / 1_000)}K`;
              return `$${v}`;
            };
            const barData = dashMonthlyTrend.map(p => ({
              name: p.short,
              mpy: Math.round(p.hasActuals ? p.actual : p.budget),
              annualBudget: Math.round(p.annualBudget),
            }));
            let cumMpy = 0, cumAnnual = 0;
            const lineData = dashMonthlyTrend.map(p => {
              cumMpy += p.hasActuals ? p.actual : p.budget;
              cumAnnual += p.annualBudget;
              return {
                name: p.short,
                cMpy: Math.round(cumMpy),
                cAnnual: Math.round(cumAnnual),
              };
            });
            const chartData = trendChartType === "bar" ? barData : lineData;
            return (
              <div className="dash-trend-collapse">
                <button
                  className="dash-trend-toggle"
                  onClick={() => setTrendChartOpen(o => !o)}
                >
                  <span className="material-icons" style={{ fontSize: 20, color: "#0891b2", transition: "transform 0.2s", transform: trendChartOpen ? "rotate(90deg)" : "rotate(0deg)" }}>
                    chevron_right
                  </span>
                  <span className="material-icons" style={{ fontSize: 20, color: "#0891b2" }}>{trendChartType === "bar" ? "bar_chart" : "show_chart"}</span>
                  {trendChartType === "bar" ? "Monthly MPY vs Annual Budget" : "Cumulative MPY vs Annual Budget"}
                  {!trendChartOpen && (
                    <span style={{ marginLeft: "auto", fontSize: 12, color: "#0891b2", fontWeight: 600, display: "flex", alignItems: "center", gap: 5, background: "oklch(0.92 0.04 200)", padding: "4px 10px", borderRadius: 6 }}>
                      <span className="material-icons" style={{ fontSize: 16 }}>visibility</span>
                      Click to view chart
                    </span>
                  )}
                  {trendChartOpen && (
                    <span className="dash-trend-type-toggle" onClick={e => { e.stopPropagation(); setTrendChartType(t => t === "bar" ? "line" : "bar"); }}>
                      <span className="material-icons" style={{ fontSize: 14 }}>{trendChartType === "bar" ? "show_chart" : "bar_chart"}</span>
                      {trendChartType === "bar" ? "Line" : "Bar"}
                    </span>
                  )}
                </button>
                {trendChartOpen && (
                  <div className="dash-trend-body">
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                      <div className="dash-chart-cat-strip" style={{ margin: 0 }}>
                        <button
                          className={`dash-chart-cat-chip${trendCategory === null ? " dash-chart-cat-chip--active" : ""}`}
                          onClick={() => setTrendCategory(null)}
                        >
                          All
                        </button>
                        {dashData.filter(cat => !EXCLUDED_CATS.has(cat.category)).map(cat => {
                          const color = CAT_COLOR(cat.category);
                          const isActive = trendCategory === cat.category;
                          return (
                            <button
                              key={cat.category}
                              className={`dash-chart-cat-chip${isActive ? " dash-chart-cat-chip--active" : ""}`}
                              style={isActive ? { background: color + "14", color, borderColor: color + "60" } : undefined}
                              onClick={() => setTrendCategory(isActive ? null : cat.category)}
                            >
                              <span className="dash-chart-cat-chip-dot" style={{ background: color }} />
                              {cat.category}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <ResponsiveContainer width="100%" height={220}>
                      {trendChartType === "bar" ? (
                        <ComposedChart data={chartData} margin={{ top: 16, right: 12, left: 0, bottom: 0 }} barCategoryGap="20%">
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                          <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#64748b" }} />
                          <YAxis axisLine={false} tickLine={false} tickFormatter={fmtAxis} tick={{ fontSize: 11, fill: "#64748b" }} width={56} />
                          <Tooltip formatter={(v: any) => fmtMoney(Number(v) || 0)} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <Bar dataKey="mpy" name="MPY (Actuals + Forecast)" fill="#0891b2" radius={[3, 3, 0, 0]}>
                            <LabelList dataKey="mpy" position="top" formatter={fmtBarLabel} style={{ fontSize: 10, fill: "#0891b2", fontWeight: 600 }} />
                          </Bar>
                          <Line type="monotone" dataKey="annualBudget" name="Annual Budget" stroke="#e85d04" strokeWidth={2.5} dot={{ r: 3.5, fill: "#e85d04", strokeWidth: 1.5, stroke: "#fff" }} />
                        </ComposedChart>
                      ) : (
                        <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                          <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#64748b" }} />
                          <YAxis axisLine={false} tickLine={false} tickFormatter={fmtAxis} tick={{ fontSize: 11, fill: "#64748b" }} width={56} />
                          <Tooltip formatter={(v: any) => fmtMoney(Number(v) || 0)} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <Line type="monotone" dataKey="cMpy" name="MPY (Cumulative)" stroke="#0891b2" strokeWidth={2.5} dot={{ r: 3, fill: "#0891b2", strokeWidth: 1.5, stroke: "#fff" }} />
                          <Line type="monotone" dataKey="cAnnual" name="Annual Budget (Cumulative)" stroke="#e85d04" strokeWidth={2} dot={false} strokeDasharray="6 3" />
                        </LineChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            );
          })()}

          {dashCCSummary.length > 0 && (() => {
            const isYTD = dashMonth === "YTD";
            const periodLabel = isYTD ? "YTD" : dashMonth;
            const refLabel = isForecastMonth ? `${dashMonth} Forecast` : "Annual Budget";
            const allCCs = dashCCSummary;
            const buGroups: Record<string, typeof allCCs> = {};
            allCCs.forEach(cc => {
              const bu = cc.business_unit || "Other";
              if (!buGroups[bu]) buGroups[bu] = [];
              buGroups[bu].push(cc);
            });

            const buNames = Object.keys(buGroups).sort();

            const varCls = (v: number) => v > 0 ? "dash-unfav" : v < 0 ? "dash-fav" : "";

            const renderNumCells = (d: { month_budget_ab?: number; month_budget: number; month_actual: number; ytd_budget_ab?: number; ytd_budget: number; ytd_actual: number }) => {
              const mAB = d.month_budget_ab || 0;
              const mFC = d.month_budget;
              const mAc = d.month_actual;
              const mVB = mAc - mAB;
              const mVF = mAc - mFC;
              const yAB = d.ytd_budget_ab || 0;
              const yFC = d.ytd_budget;
              const yAc = d.ytd_actual;
              const yVB = yAc - yAB;
              const yVF = yAc - yFC;
              return (<>
                <td className="ccx-td-num">{fmtMoney(mAB)}</td>
                <td className="ccx-td-num">{fmtMoney(mFC)}</td>
                <td className="ccx-td-num">{fmtMoney(mAc)}</td>
                <td className={`ccx-td-num ${varCls(mVB)}`}>{fmtVar(mVB)}</td>
                <td className={`ccx-td-num ${varCls(mVF)}`}>{fmtVar(mVF)}</td>
                <td className="ccx-td-num ccx-ytd">{fmtMoney(yAB)}</td>
                <td className="ccx-td-num ccx-ytd">{fmtMoney(yFC)}</td>
                <td className="ccx-td-num ccx-ytd">{fmtMoney(yAc)}</td>
                <td className={`ccx-td-num ccx-ytd ${varCls(yVB)}`}>{fmtVar(yVB)}</td>
                <td className={`ccx-td-num ccx-ytd ${varCls(yVF)}`}>{fmtVar(yVF)}</td>
              </>);
            };

            const totals = allCCs.reduce((t, c) => ({
              month_budget_ab: t.month_budget_ab + (c.month_budget_ab || 0),
              month_budget: t.month_budget + c.month_budget,
              month_actual: t.month_actual + c.month_actual,
              ytd_budget_ab: t.ytd_budget_ab + (c.ytd_budget_ab || 0),
              ytd_budget: t.ytd_budget + c.ytd_budget,
              ytd_actual: t.ytd_actual + c.ytd_actual,
            }), { month_budget_ab: 0, month_budget: 0, month_actual: 0, ytd_budget_ab: 0, ytd_budget: 0, ytd_actual: 0 });

            return (
              <div className="ccx-card">
                <div className="ccx-header">
                  <span className="material-icons" style={{ fontSize: "16px", color: "#6366f1" }}>account_tree</span>
                  <span className="ccx-title">Cost Center Variance</span>
                  <span className="ccx-subtitle">{periodLabel} · {allCCs.length} cost centers · {buNames.length} business units</span>
                </div>
                <div style={{ overflowX: "auto" }}>
                <table className="ccx-table dash-var-table" style={{ minWidth: 1500, tableLayout: "fixed" }}>
                  <colgroup>
                    <col style={{ width: 300 }} />
                    {Array.from({ length: 10 }, (_, i) => <col key={i} style={{ width: 120 }} />)}
                  </colgroup>
                  <thead className="ccx-thead--bold">
                    <tr>
                      <th className="ccx-th-left" rowSpan={2} style={{ verticalAlign: "bottom", minWidth: 240, position: "sticky", left: 0, zIndex: 2, background: "oklch(0.42 0.04 250)", cursor: "pointer", userSelect: "none" }} onClick={() => toggleCCSort("name")}>Name{sortArrow("name", ccSortCol, ccSortDir)}</th>
                      <th className="ccx-th-num" colSpan={5} style={{ textAlign: "center", borderBottom: "2px solid oklch(0.55 0.04 250)", letterSpacing: "0.08em" }}>{periodLabel}</th>
                      <th className="ccx-th-num ccx-ytd-hdr" colSpan={5} style={{ textAlign: "center", borderBottom: "2px solid oklch(0.55 0.04 250)", letterSpacing: "0.08em" }}>{periodLabel} YTD</th>
                    </tr>
                    <tr>
                      {([["mAB","Budget"],["mFC",isForecastMonth ? `${dashMonth} Fcst` : "Budget"],["mAc","Actual"],["mVB","Var vs Budget"],["mVF","Var vs Fcst"]] as [VarSortCol,string][]).map(([k,l]) => (
                        <th key={k} className="ccx-th-num" style={{ fontSize: 9, cursor: "pointer", userSelect: "none" }} onClick={() => toggleCCSort(k)}>{l}{sortArrow(k, ccSortCol, ccSortDir)}</th>
                      ))}
                      {([["yAB","Budget"],["yFC",isForecastMonth ? `${dashMonth} Fcst` : "Budget"],["yAc","Actual"],["yVB","Var vs Budget"],["yVF","Var vs Fcst"]] as [VarSortCol,string][]).map(([k,l]) => (
                        <th key={k} className="ccx-th-num ccx-ytd-hdr" style={{ fontSize: 9, cursor: "pointer", userSelect: "none" }} onClick={() => toggleCCSort(k)}>{l}{sortArrow(k, ccSortCol, ccSortDir)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {buNames.map(buName => {
                      const buCCs = buGroups[buName];
                      const buExpanded = expandedBUs.has(buName);
                      const buTotals = buCCs.reduce((t, c) => ({
                        month_budget_ab: t.month_budget_ab + (c.month_budget_ab || 0),
                        month_budget: t.month_budget + c.month_budget,
                        month_actual: t.month_actual + c.month_actual,
                        ytd_budget_ab: t.ytd_budget_ab + (c.ytd_budget_ab || 0),
                        ytd_budget: t.ytd_budget + c.ytd_budget,
                        ytd_actual: t.ytd_actual + c.ytd_actual,
                      }), { month_budget_ab: 0, month_budget: 0, month_actual: 0, ytd_budget_ab: 0, ytd_budget: 0, ytd_actual: 0 });

                      return (
                        <React.Fragment key={buName}>
                          <tr
                            className={`ccx-row${buExpanded ? " ccx-row--expanded" : ""}`}
                            style={{ background: buExpanded ? "oklch(0.92 0.02 250)" : "oklch(0.95 0.01 250)" }}
                            onClick={() => setExpandedBUs(prev => { const n = new Set(prev); n.has(buName) ? n.delete(buName) : n.add(buName); return n; })}
                          >
                            <td className="ccx-td-name" title={buName} style={{ fontWeight: 700, fontSize: 12, background: buExpanded ? "oklch(0.92 0.02 250)" : "oklch(0.95 0.01 250)" }}>
                              <span className="material-icons" style={{ fontSize: "16px", verticalAlign: "middle", marginRight: "4px", color: "#6366f1" }}>{buExpanded ? "expand_less" : "expand_more"}</span>
                              {buName}
                              <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 500, marginLeft: 6 }}>({buCCs.length})</span>
                            </td>
                            {renderNumCells(buTotals)}
                          </tr>

                          {buExpanded && [...buCCs].sort(varSorter(ccSortCol, ccSortDir)).map(cc => {
                            const ccExpanded = expandedCCs.has(cc.cost_center);
                            return (
                              <React.Fragment key={cc.cost_center}>
                                <tr
                                  className={`ccx-row${ccExpanded ? " ccx-row--expanded" : ""}`}
                                  onClick={() => setExpandedCCs(prev => { const n = new Set(prev); n.has(cc.cost_center) ? n.delete(cc.cost_center) : n.add(cc.cost_center); return n; })}
                                >
                                  <td className="ccx-td-name" title={`${cc.cost_center} – ${cc.cost_center_name}`} style={{ paddingLeft: 28 }}>
                                    <span className="material-icons" style={{ fontSize: "14px", verticalAlign: "middle", marginRight: "2px", color: "#9ca3af" }}>{ccExpanded ? "expand_less" : "expand_more"}</span>
                                    <span style={{ fontFamily: "monospace", fontSize: 10, color: "#64748b", marginRight: 6 }}>{cc.cost_center}</span>
                                    {cc.cost_center_name}
                                    {isAdmin && (
                                      <button
                                        className="ccx-comment-btn"
                                        onClick={(e) => { e.stopPropagation(); setCCCommentOpen(prev => prev === cc.cost_center ? null : cc.cost_center); }}
                                        title={ccComments[cc.cost_center] ? "Edit comment" : "Add comment"}
                                      >
                                        <span className="material-icons" style={{ fontSize: 14 }}>
                                          {ccComments[cc.cost_center] ? "comment" : "add_comment"}
                                        </span>
                                      </button>
                                    )}
                                    {!isAdmin && ccComments[cc.cost_center] && (
                                      <span className="ccx-comment-badge" title={ccComments[cc.cost_center]}>
                                        <span className="material-icons" style={{ fontSize: 13, verticalAlign: "middle" }}>comment</span>
                                      </span>
                                    )}
                                  </td>
                                  {renderNumCells(cc)}
                                </tr>

                                {ccCommentOpen === cc.cost_center && isAdmin && (
                                  <tr className="ccx-comment-row">
                                    <td colSpan={11} style={{ padding: "8px 10px 8px 44px", background: "oklch(0.96 0.01 250)" }}>
                                      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, maxWidth: "50%" }}>
                                        <textarea
                                          className="ccx-comment-textarea"
                                          value={ccCommentDrafts[cc.cost_center] || ""}
                                          onChange={(e) => setCCCommentDrafts(prev => ({ ...prev, [cc.cost_center]: e.target.value }))}
                                          onClick={(e) => e.stopPropagation()}
                                          placeholder={`Admin comment for ${cc.cost_center}…`}
                                          rows={4}
                                        />
                                        <button
                                          className="ccx-comment-save"
                                          onClick={(e) => { e.stopPropagation(); saveCCComment(cc.cost_center); }}
                                          disabled={ccCommentSaving[cc.cost_center]}
                                        >
                                          {ccCommentSaving[cc.cost_center] ? "Saving…" : "Save"}
                                        </button>
                                        <button
                                          className="ccx-comment-cancel"
                                          onClick={(e) => { e.stopPropagation(); setCCCommentOpen(null); setCCCommentDrafts(prev => ({ ...prev, [cc.cost_center]: ccComments[cc.cost_center] || "" })); }}
                                        >
                                          Cancel
                                        </button>
                                        {ccCommentFeedback[cc.cost_center] && (
                                          <span style={{ fontSize: 11, color: ccCommentFeedback[cc.cost_center] === "Saved" ? "oklch(0.55 0.18 155)" : "oklch(0.55 0.18 25)", alignSelf: "center" }}>
                                            {ccCommentFeedback[cc.cost_center]}
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                )}

                                {!isAdmin && ccComments[cc.cost_center] && (
                                  <tr className="ccx-comment-row">
                                    <td colSpan={11} style={{ padding: "4px 10px 4px 44px", background: "oklch(0.96 0.02 250)", fontSize: 11, color: "#475569" }}>
                                      <span className="material-icons" style={{ fontSize: 13, verticalAlign: "middle", marginRight: 4, color: "#6366f1" }}>comment</span>
                                      {ccComments[cc.cost_center]}
                                    </td>
                                  </tr>
                                )}

                                {ccExpanded && (cc.categories || []).filter(cat => !EXCLUDED_CATS.has(cat.category)).map(cat => {
                                  const catColor = CAT_COLOR(cat.category);
                                  const isActiveDrill = drillCC === cc.cost_center && drillCategory === cat.category;
                                  return (
                                    <React.Fragment key={`${cc.cost_center}-${cat.category}`}>
                                      <tr
                                        className={`ccx-cat-row${isActiveDrill ? " ccx-cat-row--active" : ""}`}
                                        onClick={(e) => { e.stopPropagation(); doLoadDrilldown(cat.category, cc.cost_center); }}
                                      >
                                        <td className="ccx-cat-td-name" style={{ paddingLeft: 52 }}>
                                          <span className="material-icons" style={{ fontSize: "14px", verticalAlign: "middle", marginRight: "2px", color: "#9ca3af" }}>{isActiveDrill ? "expand_less" : "expand_more"}</span>
                                          <span className="dash-cat-dot" style={{ background: catColor }} />
                                          {cat.category}
                                        </td>
                                        {renderNumCells(cat)}
                                      </tr>

                                      {isActiveDrill && drillData !== null && !drillLoading && drillData.length > 0 && drillData.filter(r => matchesAcctFilter(r.account)).map(r => {
                                        const num  = r.account.includes(" - ") ? r.account.split(" - ")[0] : "";
                                        const name = r.account.includes(" - ") ? r.account.split(" - ").slice(1).join(" - ") : r.account;
                                        return (
                                          <tr key={`${cc.cost_center}-${cat.category}-${r.account}`} className="ccx-acct-row">
                                            <td className="ccx-td-acct-name" style={{ paddingLeft: 72 }}><span className="ccx-td-acct-num">{num}</span> {name}</td>
                                            {renderNumCells({ month_budget_ab: r.month_budget_ab || 0, month_budget: r.month_budget, month_actual: r.month_actual, ytd_budget_ab: r.ytd_budget_ab || 0, ytd_budget: r.ytd_budget, ytd_actual: r.ytd_actual })}
                                          </tr>
                                        );
                                      })}
                                    </React.Fragment>
                                  );
                                })}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: "oklch(0.22 0.04 250)", fontWeight: 800 }}>
                      <td style={{ padding: "8px 10px", color: "oklch(0.92 0.01 250)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", position: "sticky", left: 0, background: "oklch(0.22 0.04 250)", zIndex: 1 }}>Total</td>
                      {(() => {
                        const mAB = totals.month_budget_ab, mFC = totals.month_budget, mAc = totals.month_actual;
                        const mVB = mAc - mAB, mVF = mAc - mFC;
                        const yAB = totals.ytd_budget_ab, yFC = totals.ytd_budget, yAc = totals.ytd_actual;
                        const yVB = yAc - yAB, yVF = yAc - yFC;
                        const c = "oklch(0.88 0.01 250)";
                        const vc = (v: number) => v > 0 ? "oklch(0.70 0.18 25)" : "oklch(0.70 0.18 155)";
                        return (<>
                          <td className="ccx-td-num" style={{ color: c }}>{fmtMoney(mAB)}</td>
                          <td className="ccx-td-num" style={{ color: c }}>{fmtMoney(mFC)}</td>
                          <td className="ccx-td-num" style={{ color: c }}>{fmtMoney(mAc)}</td>
                          <td className="ccx-td-num" style={{ color: vc(mVB) }}>{fmtVar(mVB)}</td>
                          <td className="ccx-td-num" style={{ color: vc(mVF) }}>{fmtVar(mVF)}</td>
                          <td className="ccx-td-num ccx-ytd" style={{ color: c, background: "oklch(0.18 0.04 220)" }}>{fmtMoney(yAB)}</td>
                          <td className="ccx-td-num ccx-ytd" style={{ color: c, background: "oklch(0.18 0.04 220)" }}>{fmtMoney(yFC)}</td>
                          <td className="ccx-td-num ccx-ytd" style={{ color: c, background: "oklch(0.18 0.04 220)" }}>{fmtMoney(yAc)}</td>
                          <td className="ccx-td-num ccx-ytd" style={{ color: vc(yVB), background: "oklch(0.18 0.04 220)" }}>{fmtVar(yVB)}</td>
                          <td className="ccx-td-num ccx-ytd" style={{ color: vc(yVF), background: "oklch(0.18 0.04 220)" }}>{fmtVar(yVF)}</td>
                        </>);
                      })()}
                    </tr>
                  </tfoot>
                </table>
                </div>
              </div>
            );
          })()}

          {/* ── Category Variance table ── */}
          {(() => {
            const isYTD = dashMonth === "YTD";
            const cats = dashData.filter(cat => !EXCLUDED_CATS.has(cat.category));
            const varCls = (v: number) => v > 0 ? "dash-unfav" : v < 0 ? "dash-fav" : "";
            return (
              <div className="dash-cat-table-wrap">
                <div className="ccx-header">
                  <span className="material-icons" style={{ fontSize: "16px", color: "#6366f1" }}>pie_chart</span>
                  <span className="ccx-title">Category Variance</span>
                  <span className="ccx-subtitle">{isYTD ? "YTD" : dashMonth} · click a row to filter cost centers</span>
                  <span className="ccx-count">{cats.length} categories</span>
                  <div ref={acctDropRef} style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap", position: "relative" }}>
                    {acctFilterTags.map((tag, i) => (
                      <span key={i} style={{
                        display: "inline-flex", alignItems: "center", gap: 3,
                        background: "oklch(0.92 0.04 260)", color: "oklch(0.30 0.08 260)",
                        borderRadius: 4, padding: "2px 6px", fontSize: 10, fontWeight: 600,
                      }}>
                        {tag}
                        <span
                          className="material-icons"
                          style={{ fontSize: 12, cursor: "pointer", color: "oklch(0.50 0.06 260)" }}
                          onClick={() => setAcctFilterTags(prev => prev.filter((_, j) => j !== i))}
                        >close</span>
                      </span>
                    ))}
                    <span className="material-icons" style={{ fontSize: 15, color: "oklch(0.55 0.02 250)" }}>search</span>
                    <input
                      ref={acctInputRef}
                      type="text"
                      placeholder={acctFilterTags.length > 0 ? "Add more…" : "Filter accounts…"}
                      value={acctSearch}
                      onChange={e => { setAcctSearch(e.target.value); setAcctDropdownOpen(true); }}
                      onFocus={() => setAcctDropdownOpen(true)}
                      onKeyDown={e => {
                        if (e.key === "Backspace" && !acctSearch && acctFilterTags.length > 0) {
                          setAcctFilterTags(prev => prev.slice(0, -1));
                        } else if (e.key === "Escape") {
                          setAcctDropdownOpen(false);
                        }
                      }}
                      style={{
                        width: 170, padding: "4px 8px", fontSize: 11, fontWeight: 500,
                        border: "1px solid oklch(0.85 0.01 250)", borderRadius: 6,
                        background: "oklch(0.98 0.003 250)", color: "oklch(0.20 0.02 250)",
                        outline: "none",
                      }}
                    />
                    {acctFilterTags.length > 0 && (
                      <button
                        onClick={() => { setAcctFilterTags([]); setAcctSearch(""); }}
                        style={{ background: "none", border: "none", cursor: "pointer", display: "flex", padding: 2 }}
                        title="Clear all filters"
                      >
                        <span className="material-icons" style={{ fontSize: 14, color: "oklch(0.55 0.02 250)" }}>close</span>
                      </button>
                    )}
                    {acctDropdownOpen && acctSuggestions.length > 0 && (
                      <div style={{
                        position: "absolute", right: 0, top: "100%", marginTop: 4,
                        background: "oklch(0.99 0.002 250)", border: "1px solid oklch(0.88 0.01 250)",
                        borderRadius: 8, boxShadow: "0 6px 20px oklch(0.20 0.02 250 / 0.15)",
                        zIndex: 50, minWidth: 280, maxHeight: 260, overflowY: "auto",
                      }}>
                        {acctSuggestions.map(a => {
                          const already = acctFilterTags.includes(a.account);
                          return (
                            <div
                              key={a.account}
                              onClick={() => {
                                if (!already) setAcctFilterTags(prev => [...prev, a.account]);
                                setAcctSearch("");
                                setAcctDropdownOpen(false);
                                acctInputRef.current?.focus();
                              }}
                              style={{
                                padding: "6px 12px", fontSize: 11, cursor: already ? "default" : "pointer",
                                display: "flex", justifyContent: "space-between", alignItems: "center",
                                color: already ? "oklch(0.65 0.02 250)" : "oklch(0.20 0.02 250)",
                                background: already ? "oklch(0.96 0.01 250)" : "transparent",
                              }}
                              onMouseEnter={e => { if (!already) e.currentTarget.style.background = "oklch(0.95 0.02 260)"; }}
                              onMouseLeave={e => { e.currentTarget.style.background = already ? "oklch(0.96 0.01 250)" : "transparent"; }}
                            >
                              <span>{a.account}</span>
                              <span style={{ fontSize: 9, color: "oklch(0.55 0.02 250)", fontWeight: 500 }}>{a.category}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ overflowX: "auto" }}>
                <table className="dash-cat-table dash-var-table" style={{ minWidth: 1340, tableLayout: "fixed" }}>
                  <colgroup>
                    <col style={{ width: 240 }} />
                    {Array.from({ length: 10 }, (_, i) => <col key={i} style={{ width: 110 }} />)}
                  </colgroup>
                  <thead className="ccx-thead--bold">
                    <tr>
                      <th className="ccx-th-left" rowSpan={2} style={{ verticalAlign: "bottom", minWidth: 200, position: "sticky", left: 0, zIndex: 2, background: "oklch(0.42 0.04 250)", cursor: "pointer", userSelect: "none" }} onClick={() => toggleCatSort("name")}>Category{sortArrow("name", catSortCol, catSortDir)}</th>
                      <th className="ccx-th-num" colSpan={5} style={{ textAlign: "center", borderBottom: "2px solid oklch(0.55 0.04 250)", letterSpacing: "0.08em" }}>{isYTD ? "YTD" : dashMonth}</th>
                      <th className="ccx-th-num ccx-ytd-hdr" colSpan={5} style={{ textAlign: "center", borderBottom: "2px solid oklch(0.55 0.04 250)", letterSpacing: "0.08em" }}>{isYTD ? "YTD" : dashMonth} YTD</th>
                    </tr>
                    <tr>
                      {([["mAB","Budget"],["mFC",isForecastMonth ? `${dashMonth} Fcst` : "Budget"],["mAc","Actual"],["mVB","Var vs Budget"],["mVF","Var vs Fcst"]] as [VarSortCol,string][]).map(([k,l]) => (
                        <th key={k} className="ccx-th-num" style={{ fontSize: 9, cursor: "pointer", userSelect: "none" }} onClick={() => toggleCatSort(k)}>{l}{sortArrow(k, catSortCol, catSortDir)}</th>
                      ))}
                      {([["yAB","Budget"],["yFC",isForecastMonth ? `${dashMonth} Fcst` : "Budget"],["yAc","Actual"],["yVB","Var vs Budget"],["yVF","Var vs Fcst"]] as [VarSortCol,string][]).map(([k,l]) => (
                        <th key={k} className="ccx-th-num ccx-ytd-hdr" style={{ fontSize: 9, cursor: "pointer", userSelect: "none" }} onClick={() => toggleCatSort(k)}>{l}{sortArrow(k, catSortCol, catSortDir)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...cats].sort(varSorter(catSortCol, catSortDir)).map(cat => {
                      const color     = CAT_COLOR(cat.category);
                      const isActive  = drillCategory === cat.category;

                      const mAB = cat.month_budget_ab || 0;
                      const mFC = cat.month_budget;
                      const mAc = cat.month_actual;
                      const mVB = mAc - mAB;
                      const mVF = mAc - mFC;
                      const yAB = cat.ytd_budget_ab || 0;
                      const yFC = cat.ytd_budget;
                      const yAc = cat.ytd_actual;
                      const yVB = yAc - yAB;
                      const yVF = yAc - yFC;

                      return (
                        <React.Fragment key={cat.category}>
                          <tr
                            className={`dash-cat-tr${isActive ? " dash-cat-tr--active" : ""}`}
                            onClick={() => {
                              if (drillCategory === cat.category && !drillCC) {
                                setDrillCategory(null); setDrillCC(null); setDrillData(null);
                              } else {
                                setDrillCategory(cat.category); setDrillCC(null); setDrillData(null);
                              }
                            }}
                            title={`Click to see cost centers for ${cat.category}`}
                          >
                            <td className="dash-cat-td-name">
                              <span className="material-icons dash-cat-expand-icon">{isActive ? "expand_less" : "expand_more"}</span>
                              <span className="dash-cat-dot" style={{ background: color }} />
                              {cat.category}
                            </td>
                            <td className="ccx-td-num">{fmtMoney(mAB)}</td>
                            <td className="ccx-td-num">{fmtMoney(mFC)}</td>
                            <td className="ccx-td-num">{fmtMoney(mAc)}</td>
                            <td className={`ccx-td-num ${varCls(mVB)}`}>{fmtVar(mVB)}</td>
                            <td className={`ccx-td-num ${varCls(mVF)}`}>{fmtVar(mVF)}</td>
                            <td className="ccx-td-num ccx-ytd">{fmtMoney(yAB)}</td>
                            <td className="ccx-td-num ccx-ytd">{fmtMoney(yFC)}</td>
                            <td className="ccx-td-num ccx-ytd">{fmtMoney(yAc)}</td>
                            <td className={`ccx-td-num ccx-ytd ${varCls(yVB)}`}>{fmtVar(yVB)}</td>
                            <td className={`ccx-td-num ccx-ytd ${varCls(yVF)}`}>{fmtVar(yVF)}</td>
                          </tr>

                          {/* Expanded: Cost Center rows as flat siblings in the same table */}
                          {isActive && (() => {
                            const ccRows = (cat as any).cc_breakdown || [...(cat.top_over || []), ...(cat.top_under || [])];
                            const uniqueCCs = ccRows.reduce((map: any, row: any) => {
                              if (!map[row.cost_center]) map[row.cost_center] = row;
                              return map;
                            }, {} as Record<string, any>);
                            const ccList = Object.values(uniqueCCs) as any[];
                            return ccList.map((ccRow: any) => {
                              const cmAB = ccRow.month_budget_ab || 0;
                              const cmFC = ccRow.month_budget;
                              const cmAc = ccRow.month_actual;
                              const cmVB = cmAc - cmAB;
                              const cmVF = cmAc - cmFC;
                              const cyAB = ccRow.ytd_budget_ab || 0;
                              const cyFC = ccRow.ytd_budget;
                              const cyAc = ccRow.ytd_actual;
                              const cyVB = cyAc - cyAB;
                              const cyVF = cyAc - cyFC;
                              const isCCActive = drillCC === ccRow.cost_center;
                              return (
                                <React.Fragment key={`cc-${ccRow.cost_center}`}>
                                  <tr
                                    className={`ccx-cc-row${isCCActive ? " ccx-cc-row--active" : ""}`}
                                    onClick={(e) => { e.stopPropagation(); doLoadDrilldown(cat.category, ccRow.cost_center); }}
                                    title={`Click to see accounts for CC ${ccRow.cost_center}`}
                                  >
                                    <td className="dash-cat-td-name ccx-cc-td-name">
                                      <span className="material-icons dash-cat-expand-icon">{isCCActive ? "expand_less" : "expand_more"}</span>
                                      <span className="ccx-td-acct-num">{ccRow.cost_center}</span>
                                      <span className="ccx-cc-name">{ccRow.cost_center_name}</span>
                                    </td>
                                    <td className="ccx-td-num">{fmtMoney(cmAB)}</td>
                                    <td className="ccx-td-num">{fmtMoney(cmFC)}</td>
                                    <td className="ccx-td-num">{fmtMoney(cmAc)}</td>
                                    <td className={`ccx-td-num ${varCls(cmVB)}`}>{fmtVar(cmVB)}</td>
                                    <td className={`ccx-td-num ${varCls(cmVF)}`}>{fmtVar(cmVF)}</td>
                                    <td className="ccx-td-num ccx-ytd">{fmtMoney(cyAB)}</td>
                                    <td className="ccx-td-num ccx-ytd">{fmtMoney(cyFC)}</td>
                                    <td className="ccx-td-num ccx-ytd">{fmtMoney(cyAc)}</td>
                                    <td className={`ccx-td-num ccx-ytd ${varCls(cyVB)}`}>{fmtVar(cyVB)}</td>
                                    <td className={`ccx-td-num ccx-ytd ${varCls(cyVF)}`}>{fmtVar(cyVF)}</td>
                                  </tr>
                                  {isCCActive && drillLoading && (
                                    <tr className="ccx-acct-loading-row"><td colSpan={11}>Loading accounts…</td></tr>
                                  )}
                                  {isCCActive && drillData !== null && !drillLoading && drillData.length > 0 && drillData.filter(r => matchesAcctFilter(r.account)).map(r => {
                                    const num  = r.account.includes(" - ") ? r.account.split(" - ")[0] : "";
                                    const name = r.account.includes(" - ") ? r.account.split(" - ").slice(1).join(" - ") : r.account;
                                    const amAB = r.month_budget_ab || 0;
                                    const amFC = r.month_budget;
                                    const amAc = r.month_actual;
                                    const amVB = amAc - amAB;
                                    const amVF = amAc - amFC;
                                    const ayAB = r.ytd_budget_ab || 0;
                                    const ayFC = r.ytd_budget;
                                    const ayAc = r.ytd_actual;
                                    const ayVB = ayAc - ayAB;
                                    const ayVF = ayAc - ayFC;
                                    return (
                                      <tr key={r.account} className="ccx-acct-row">
                                        <td className="dash-cat-td-name ccx-acct-td-name">
                                          <span className="ccx-td-acct-num">{num}</span> {name}
                                        </td>
                                        <td className="ccx-td-num">{fmtMoney(amAB)}</td>
                                        <td className="ccx-td-num">{fmtMoney(amFC)}</td>
                                        <td className="ccx-td-num">{fmtMoney(amAc)}</td>
                                        <td className={`ccx-td-num ${varCls(amVB)}`}>{fmtVar(amVB)}</td>
                                        <td className={`ccx-td-num ${varCls(amVF)}`}>{fmtVar(amVF)}</td>
                                        <td className="ccx-td-num ccx-ytd">{fmtMoney(ayAB)}</td>
                                        <td className="ccx-td-num ccx-ytd">{fmtMoney(ayFC)}</td>
                                        <td className="ccx-td-num ccx-ytd">{fmtMoney(ayAc)}</td>
                                        <td className={`ccx-td-num ccx-ytd ${varCls(ayVB)}`}>{fmtVar(ayVB)}</td>
                                        <td className={`ccx-td-num ccx-ytd ${varCls(ayVF)}`}>{fmtVar(ayVF)}</td>
                                      </tr>
                                    );
                                  })}
                                </React.Fragment>
                              );
                            });
                          })()}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </div>
            );
          })()}
        </>
      )}

      {/* ── Variance Prompt Modal ─────────────────────────────────────────── */}
      {promptModalOpen && (
        <div className="vp-overlay" onClick={() => { setPromptModalOpen(false); setPromptCopied(false); }}>
          <div className="vp-modal" onClick={e => e.stopPropagation()}>
            <div className="vp-modal-header">
              <span className="material-icons">auto_awesome</span>
              <span>Variance Report — AI Prompt</span>
              <button
                style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#6b7280", display: "flex", alignItems: "center", padding: "4px", borderRadius: "6px" }}
                onClick={() => { setPromptModalOpen(false); setPromptCopied(false); }}
              >
                <span className="material-icons">close</span>
              </button>
            </div>

            <div className="vp-modal-body">
              {promptLoading ? (
                <div style={{ padding: "32px", textAlign: "center", color: "#9ca3af", fontSize: "13px" }}>
                  <span className="material-icons fv-spin" style={{ fontSize: 20, marginBottom: 8, display: "block" }}>sync</span>
                  Gathering variance data...
                </div>
              ) : (
                <>
                  <p className="vp-modal-desc">
                    Copy this prompt and paste it into ChatGPT or Claude to generate a narrative variance report for leadership.
                  </p>
                  <textarea
                    className="vp-prompt-textarea"
                    value={promptText}
                    readOnly
                    rows={18}
                    onClick={e => (e.target as HTMLTextAreaElement).select()}
                  />
                  <div className="vp-modal-footer">
                    <span className="vp-char-count">
                      {promptText.length.toLocaleString()} characters · ~{Math.ceil(promptText.length / 4).toLocaleString()} tokens
                    </span>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button className="admin-btn admin-btn--ghost" onClick={() => { setPromptModalOpen(false); setPromptCopied(false); }}>
                        Close
                      </button>
                      <button
                        className="admin-btn admin-btn--primary"
                        onClick={() => {
                          navigator.clipboard.writeText(promptText).then(() => {
                            setPromptCopied(true);
                            setTimeout(() => setPromptCopied(false), 2500);
                          });
                        }}
                      >
                        <span className="material-icons" style={{ fontSize: 16 }}>
                          {promptCopied ? "check" : "content_copy"}
                        </span>
                        {promptCopied ? "Copied!" : "Copy to Clipboard"}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActualsDashboard;
